/**
 * Builds complete CollectionPage ItemList JSON-LD from /collections/{handle}/products.json
 * so schema includes all collection variants without changing PLP pagination.
 */
(function () {
  const configEl = document.getElementById('collection-schema-config');
  const schemaEl = document.getElementById('collection-schema-jsonld');

  if (!configEl || !schemaEl) {
    return;
  }

  /** @type {Record<string, unknown>} */
  let config;

  try {
    config = JSON.parse(configEl.textContent || '{}');
  } catch {
    return;
  }

  if (config.completeOnServer === true) {
    return;
  }

  const EXCLUDED_HANDLES = new Set(['gift-wrap']);

  /**
   * @param {string} handle
   * @returns {Promise<Record<string, unknown>[]>}
   */
  const fetchAllProducts = async (handle) => {
    const products = [];
    let page = 1;

    while (true) {
      const url = new URL(`/collections/${handle}/products.json`, window.location.origin);
      url.searchParams.set('limit', '250');
      url.searchParams.set('page', String(page));

      const response = await fetch(url.toString(), { credentials: 'same-origin' });

      if (!response.ok) {
        break;
      }

      const data = await response.json();

      if (!data.products || data.products.length === 0) {
        break;
      }

      products.push(...data.products);

      if (data.products.length < 250) {
        break;
      }

      page += 1;
    }

    return products;
  };

  /**
   * @param {Record<string, unknown>} product
   * @param {Record<string, unknown>} variant
   * @returns {Record<string, unknown>}
   */
  const buildListItem = (product, variant) => {
    const baseUrl = String(config.baseUrl || '');
    const handle = String(product.handle || '');
    const variantId = variant.id;
    const productTitle = String(product.title || '');
    const variantTitle = String(variant.title || '');
    let itemName = productTitle;

    if (variantTitle && variantTitle !== 'Default Title') {
      itemName = `${productTitle} - ${variantTitle}`;
    }

    const variantUrl = `${baseUrl}/products/${handle}?variant=${variantId}`;
    const variantImage = variant.featured_image || product.images?.[0];
    const imageSrc = variantImage && typeof variantImage === 'object' ? variantImage.src : null;
    const price = parseFloat(String(variant.price || '0'));
    const available = variant.available === true;

    /** @type {Record<string, unknown>} */
    const item = {
      '@type': 'Product',
      '@id': variantUrl,
      name: itemName,
      url: variantUrl,
      brand: {
        '@type': 'Brand',
        name: config.brandName,
      },
      category: config.category,
      offers: {
        '@type': 'Offer',
        url: variantUrl,
        price,
        priceCurrency: config.currency,
        availability: available ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        itemCondition: 'https://schema.org/NewCondition',
        seller: {
          '@type': 'Organization',
          name: config.brandName,
        },
      },
    };

    if (imageSrc) {
      item.image = imageSrc;
    }

    return item;
  };

  /**
   * @param {Record<string, unknown>[]} products
   * @returns {Record<string, unknown>[]}
   */
  const buildItemListElements = (products) => {
    const elements = [];
    let position = 0;

    for (const product of products) {
      const handle = String(product.handle || '');

      if (EXCLUDED_HANDLES.has(handle)) {
        continue;
      }

      if (product.available === false) {
        continue;
      }

      const variants = Array.isArray(product.variants) ? product.variants : [];

      for (const variant of variants) {
        if (variant.available === false) {
          continue;
        }

        position += 1;
        elements.push({
          '@type': 'ListItem',
          position,
          item: buildListItem(product, variant),
        });
      }
    }

    return elements;
  };

  /**
   * @param {Record<string, unknown>[]} itemListElement
   */
  const renderSchema = (itemListElement) => {
    const graph = [
      {
        '@type': 'CollectionPage',
        '@id': config.collectionPageId,
        url: config.collectionUrl,
        name: config.name,
        ...(config.description ? { description: config.description } : {}),
        inLanguage: config.locale,
        isPartOf: {
          '@type': 'WebSite',
          '@id': config.websiteId,
          name: config.brandName,
          url: config.websiteUrl,
        },
        about: {
          '@type': 'Thing',
          name: config.aboutName,
        },
        ...(config.imageUrl
          ? {
              primaryImageOfPage: {
                '@type': 'ImageObject',
                url: config.imageUrl,
              },
            }
          : {}),
        mainEntity: {
          '@id': config.itemListId,
        },
      },
      {
        '@type': 'ItemList',
        '@id': config.itemListId,
        name: config.collectionTitle,
        url: config.collectionUrl,
        itemListOrder: 'https://schema.org/ItemListOrderAscending',
        numberOfItems: itemListElement.length,
        itemListElement,
      },
    ];

    schemaEl.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': graph,
    });
  };

  const init = async () => {
    try {
      const products = await fetchAllProducts(String(config.handle || ''));

      if (products.length === 0) {
        return;
      }

      const itemListElement = buildItemListElements(products);
      renderSchema(itemListElement);
    } catch {
      // Keep server-rendered fallback schema on failure.
    }
  };

  init();
})();
